import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';

const OrderingTab = ({ productId }) => {
  const [showScrollHint, setShowScrollHint] = useState(true);
  const tableRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (tableRef.current) {
        const isScrollable = tableRef.current.scrollWidth > tableRef.current.clientWidth;
        const isScrolledToEnd = tableRef.current.scrollLeft + tableRef.current.clientWidth >= tableRef.current.scrollWidth;
        setShowScrollHint(isScrollable && !isScrolledToEnd);
      }
    };

    if (tableRef.current) {
      handleScroll();
      tableRef.current.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      if (tableRef.current) {
        tableRef.current.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      }
    };
  }, []);

  const products = {
    RUT956: {
      name: 'RUT956',
      ordering: {
        standardPackage: [
          { id: 1, name: 'RUT956 Router' },
          { id: 2, name: '9 W PSU' },
          { id: 3, name: '2 x Mobile antennas (magnetic mount, SMA male, 3 m cable)' },
          { id: 4, name: '2 x Wi-Fi antennas (magnetic mount, RP-SMA male, 1.5 m cable)' },
          { id: 5, name: 'GNSS antenna (adhesive, SMA male, 3 m cable)' },
          { id: 6, name: 'RS485 connector block' },
          { id: 7, name: 'I/O connector block' },
          { id: 8, name: 'Ethernet cable (1.5 m)' },
          { id: 9, name: 'SIM Adapter kit' },
          { id: 10, name: 'QSG (Quick Start Guide)' },
          { id: 11, name: 'Packaging box' }
        ],
        classificationCodes: [
          { label: 'HS Code', value: '851762' },
          { label: 'HTS', value: '8517.62.00' }
        ],
        availableVersions: [
          {
            hardwareVersion: 'RUT956 1*****',
            regions: ['Europe¹', 'The Middle East', 'Africa', 'Korea', 'Thailand'],
            frequencies: {
              '4G (LTE-FDD)': ['B1', 'B3', 'B5', 'B7', 'B8', 'B20'],
              '4G (LTE-TDD)': ['B40'],
              '3G': ['B1', 'B5', 'B8'],
              '2G': ['B3', 'B8']
            },
            orderCodes: [
              { code: 'RUT956100000', description: 'Standard package with EU PSU' },
              { code: 'RUT956100100', description: 'Standard package with UK PSU' },
              { code: 'RUT956100300', description: 'Standard package with Power cable with 4-way screw terminal' },
              { code: 'RUT956100200', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT956 2*****',
            regions: ['Europe¹', 'The Middle East', 'Africa', 'Korea', 'Thailand'],
            frequencies: {
              '4G (LTE-FDD)': ['B1', 'B3', 'B7', 'B8', 'B20', 'B28A'],
              '4G (LTE-TDD)': ['B38', 'B40', 'B41'],
              '3G': ['B1', 'B8'],
              '2G': ['B3', 'B8']
            },
            orderCodes: [
              { code: 'RUT956200000', description: 'Standard package with EU PSU' },
              { code: 'RUT956200100', description: 'Standard package with UK PSU' },
              { code: 'RUT956200300', description: 'Standard package with Power cable with 4-way screw terminal' },
              { code: 'RUT956200500', description: 'Standard package with Universal PSU' },
              { code: 'RUT956200200', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT956 4*****',
            regions: ['Global¹'],
            frequencies: {
              '4G (LTE-FDD)': ['B1', 'B2', 'B3', 'B4', 'B5', 'B7', 'B8', 'B12', 'B13', 'B18', 'B19', 'B20', 'B25', 'B26', 'B28'],
              '4G (LTE-TDD)': ['B38', 'B39', 'B40', 'B41'],
              '3G': ['B1', 'B2', 'B4', 'B5', 'B6', 'B8', 'B19'],
              '2G': ['B2', 'B3', 'B5', 'B8']
            },
            orderCodes: [
              { code: 'RUT956400V00', description: 'Standard package with EU PSU' },
              { code: 'RUT956400500', description: 'Standard package with Universal PSU' },
              { code: 'RUT956400200', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT956 7*****',
            regions: ['South America', 'Australia', 'New Zealand', 'Taiwan'],
            frequencies: {
              '4G (LTE-FDD)': ['B1', 'B2', 'B3', 'B4', 'B5', 'B7', 'B8', 'B28'],
              '4G (LTE-TDD)': ['B40'],
              '3G': ['B1', 'B2', 'B4', 'B5', 'B8'],
              '2G': ['B2', 'B3', 'B5', 'B8']
            },
            orderCodes: [
              { code: 'RUT956700700', description: 'Standard package with AU PSU' },
              { code: 'RUT956700000', description: 'Standard package with EU PSU' },
              { code: 'RUT956700500', description: 'Standard package with Universal PSU' },
              { code: 'RUT956700A00', description: 'Standard package with US PSU' },
              { code: 'RUT956700200', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT956 9*****',
            regions: ['Japan'],
            frequencies: {
              '4G (LTE-FDD)': ['B1', 'B3', 'B8', 'B18', 'B19', 'B26'],
              '4G (LTE-TDD)': ['B41'],
              '3G': ['B1', 'B6', 'B8', 'B19']
            },
            orderCodes: [
              { code: 'RUT956900C00', description: 'Standard package with JP PSU' },
              { code: 'RUT956900200', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT956 A*****',
            regions: ['North America²'],
            frequencies: {
              '4G (LTE-FDD)': ['B2', 'B4', 'B5', 'B12', 'B13', 'B14', 'B66', 'B71'],
              '3G': ['B2', 'B4', 'B5']
            },
            orderCodes: [
              { code: 'RUT956A00A00', description: 'Standard package with US PSU' },
              { code: 'RUT956A00200', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT956 200505',
            regions: ['Thailand'],
            frequencies: {
              '4G (LTE-FDD)': ['B1', 'B3', 'B7', 'B8', 'B20'],
              '4G (LTE-TDD)': ['B38', 'B40'],
              '3G': ['B1', 'B8'],
              '2G': ['B3', 'B8']
            },
            orderCodes: [
              { code: 'RUT956200505', description: 'Standard package with Universal PSU' }
            ]
          }
        ],
        footnotes: [
          { id: 1, text: 'Regional availability - excluding Russia & Belarus' },
          { id: 2, text: 'For more detailed information about certified carriers, visit our Wiki page' }
        ]
      }
    },
    RUT241: {
      name: 'RUT241',
      ordering: {
        standardPackage: [
          { id: 1, name: 'RUT241 Router' },
          { id: 2, name: '9 W PSU' },
          { id: 3, name: '2 × 4G Mobile antennas (swivel, SMA male)' },
          { id: 4, name: '1 x WiFi antenna (swivel, RP-SMA male)' },
          { id: 5, name: 'Ethernet cable (1.5 m)' },
          { id: 6, name: 'SIM Adapter kit' },
          { id: 7, name: 'QSG (Quick Start Guide)' },
          { id: 8, name: 'Packaging box' }
        ],
        classificationCodes: [
          { label: 'HS Code', value: '851762' },
          { label: 'HTS', value: '8517.62.00' }
        ],
        availableVersions: [
          {
            hardwareVersion: 'RUT241 0***',
            regions: ['Europe¹', 'Middle East', 'Africa', 'Thailand'],
            frequencies: {
              'LTE-FDD': ['B1', 'B3', 'B7', 'B8', 'B20', 'B28A'],
              '3G': ['B1', 'B8'],
              '2G': ['B3', 'B8']
            },
            orderCodes: [
              { code: 'RUT241000000', description: 'Standard package with EU PSU' },
              { code: 'RUT241001000', description: 'Standard package with UK PSU' },
              { code: 'RUT241004000', description: 'Standard package with Power cable with 4-way screw terminal' },
              { code: 'RUT241002030', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT241 1***',
            regions: ['Europe¹', 'Middle East', 'Africa'],
            frequencies: {
              'LTE-FDD': ['B1', 'B3', 'B5', 'B7', 'B8', 'B20'],
              'LTE-TDD': ['B40'],
              '3G': ['B1', 'B5', 'B8'],
              '2G': ['B3', 'B8']
            },
            orderCodes: [
              { code: 'RUT241010000', description: 'Standard package with EU PSU' },
              { code: 'RUT241011000', description: 'Standard package with UK PSU' },
              { code: 'RUT241012030', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT241 3***',
            regions: ['Global¹'],
            frequencies: {
              'LTE-FDD': ['B1', 'B2', 'B3', 'B4', 'B5', 'B7', 'B8', 'B12', 'B13', 'B18', 'B19', 'B20', 'B25', 'B26', 'B28'],
              'LTE-TDD': ['B38', 'B39', 'B40', 'B41'],
              '3G': ['B1', 'B2', 'B4', 'B5', 'B6', 'B8', 'B19'],
              '2G': ['B2', 'B3', 'B5', 'B8']
            },
            orderCodes: [
              { code: 'RUT2410300G0', description: 'Standard package with EU PSU' },
              { code: 'RUT241034000', description: 'Standard package with Power cable with 4-way screw terminal' },
              { code: 'RUT241033000', description: 'Standard package with Universal PSU' },
              { code: 'RUT241038000', description: 'Standard package with US PSU' },
              { code: 'RUT241032030', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT241 6***',
            regions: ['Australia', 'New Zealand', 'Taiwan'],
            frequencies: {
              'LTE-FDD': ['B1', 'B3', 'B5', 'B7', 'B8', 'B28'],
              'LTE-TDD': ['B40'],
              '3G': ['B1', 'B5', 'B8'],
              '2G': ['B3', 'B5', 'B8']
            },
            orderCodes: [
              { code: 'RUT241065000', description: 'Standard package with AU PSU' },
              { code: 'RUT241063000', description: 'Standard package with Universal PSU' },
              { code: 'RUT241062030', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT241 7***',
            regions: ['Latin America'],
            frequencies: {
              'LTE-FDD': ['B1', 'B2', 'B3', 'B4', 'B5', 'B7', 'B8', 'B28'],
              'LTE-TDD': ['B40'],
              '3G': ['B1', 'B2', 'B4', 'B5', 'B8'],
              '2G': ['B2', 'B3', 'B5', 'B8']
            },
            orderCodes: [
              { code: 'RUT241070000', description: 'Standard package with EU PSU' },
              { code: 'RUT241073000', description: 'Standard package with Universal PSU' },
              { code: 'RUT241075000', description: 'Standard package with AU PSU' },
              { code: 'RUT241072030', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT241 8***',
            regions: ['Japan'],
            frequencies: {
              'LTE-FDD': ['B1', 'B3', 'B8', 'B18', 'B19', 'B26'],
              'LTE-TDD': ['B41'],
              '3G': ['B1', 'B6', 'B8', 'B19']
            },
            orderCodes: [
              { code: 'RUT241087000', description: 'Standard package with JP PSU' },
              { code: 'RUT241082030', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT241 9***',
            regions: ['North America²'],
            frequencies: {
              'LTE-FDD': ['B2', 'B4', 'B5', 'B12', 'B13', 'B14', 'B66', 'B71'],
              '3G': ['B2', 'B4', 'B5']
            },
            orderCodes: [
              { code: 'RUT241098000', description: 'Standard package with US PSU' },
              { code: 'RUT241092030', description: 'Mass packing code' }
            ]
          }
        ],
        footnotes: [
          { id: 1, text: 'Regional availability - excluding Russia & Belarus' },
          { id: 2, text: 'Excluding China/Japan' }
        ]
      }
    },
    RUT241_eSIM: {
      name: 'RUT241 eSIM',
      ordering: {
        standardPackage: [
          { id: 1, name: 'Router RUT241' },
          { id: 2, name: '9 W PSU' },
          { id: 3, name: '2x Mobile antennas (swivel, SMA male)' },
          { id: 4, name: '1x Wi-Fi antenna (swivel, RP-SMA male)' },
          { id: 5, name: 'Ethernet cable (1.5 m)' },
          { id: 6, name: 'SIM Adapter kit' },
          { id: 7, name: 'QSG (Quick Start Guide)' },
          { id: 8, name: 'Packaging box' }
        ],
        classificationCodes: [
          { label: 'HS Code', value: '851762' },
          { label: 'HTS', value: '8517.62.00' }
        ],
        availableVersions: [
          {
            hardwareVersion: 'RUT241 *1****',
            regions: ['Europe¹'],
            notes: 'TPM optional - different hardware required; contact your sales manager',
            frequencies: {
              '4G (LTE-FDD)': ['B1', 'B3', 'B5', 'B7', 'B8', 'B20'],
              '4G (LTE-TDD)': ['B40'],
              '3G': ['B1', 'B5', 'B8'],
              '2G': ['B3', 'B8']
            },
            orderCodes: [
              { code: 'RUT241210000', description: 'Standard package with EU PSU' },
              { code: 'RUT241212030', description: 'Mass packing code' }
            ]
          },
          {
            hardwareVersion: 'RUT241 *3****',
            regions: ['Global¹'],
            notes: 'TPM optional - different hardware required; contact your sales manager',
            frequencies: {
              '4G (LTE-FDD)': ['B1', 'B2', 'B3', 'B4', 'B5', 'B7', 'B8', 'B12', 'B13', 'B18', 'B19', 'B20', 'B25', 'B26', 'B28'],
              '4G (LTE-TDD)': ['B38', 'B39', 'B40', 'B41'],
              '3G': ['B1', 'B2', 'B4', 'B5', 'B6', 'B8', 'B19'],
              '2G': ['B2', 'B3', 'B5', 'B8']
            },
            orderCodes: [
              { code: 'RUT241230000', description: 'Standard package with EU PSU' },
              { code: 'RUT241238000', description: 'Standard package with US PSU' },
              { code: 'RUT241235000', description: 'Standard package with AU PSU' },
              { code: 'RUT241233000', description: 'Standard package with Universal PSU' }
            ]
          },
          {
            hardwareVersion: 'RUT241 *9****',
            regions: ['North America'],
            notes: 'TPM optional - different hardware required; contact your sales manager',
            frequencies: {
              '4G (LTE-FDD)': ['B2', 'B4', 'B12', 'B13', 'B14', 'B66', 'B71'],
              '3G': ['B2', 'B4', 'B5']
            },
            orderCodes: [
              { code: 'RUT241298000', description: 'Standard package with US PSU' },
              { code: 'RUT241292030', description: 'Mass packing code' }
            ]
          }
        ],
        footnotes: [
          { id: 1, text: 'Regional availability - excluding Russia & Belarus' },
          { id: 2, text: 'For more detailed information about certified carriers, visit our Wiki page' }
        ]
      }
    },
    RUTm50: {
      name: 'RUTm50',
      ordering: {
        standardPackage: [
          { id: 1, name: 'RUTM50 Router' },
          { id: 2, name: '18 W PSU' },
          { id: 3, name: '4 x Mobile antennas (swivel, SMA male)' },
          { id: 4, name: '2 x Wi-Fi antennas (magnetic mount, RP-SMA male, 1.5 m cable)' },
          { id: 5, name: '1 x GNSS antenna (adhesive, SMA male, 3 m cable)' },
          { id: 6, name: 'Ethernet cable (1.5 m)' },
          { id: 7, name: 'SIM Adapter kit' },
          { id: 8, name: 'QSG (Quick Start Guide)' },
          { id: 9, name: 'Packaging box' }
        ],
        classificationCodes: [
          { label: 'HS Code', value: '851762' },
          { label: 'HTS', value: '8517.62.00' }
        ],
        availableVersions: [
          {
            hardwareVersion: 'RUTM50 0*****',
            regions: ['North America²'],
            frequencies: {
              '5G NR NSA': [
                'n2', 'n5', 'n7', 'n12', 'n13', 'n14', 'n25', 'n26', 
                'n29', 'n30', 'n38', 'n41', 'n48', 'n66', 'n70', 'n71', 
                'n77', 'n78'
              ],
              '5G NR SA': [
                'n2', 'n5', 'n7', 'n12', 'n13', 'n14', 'n25', 'n26', 
                'n29', 'n30', 'n38', 'n41', 'n48', 'n66', 'n70', 'n71', 
                'n77', 'n78'
              ],
              '5G DL 4 × 4 MIMO': [
                'n2', 'n5', 'n7', 'n12', 'n13*', 'n14', 'n25', 'n26*', 
                'n29', 'n30', 'n38', 'n41', 'n48', 'n66', 'n70', 'n71', 
                'n77', 'n78'
              ],
              '4G (LTE-FDD)': [
                'B2', 'B4', 'B5', 'B7', 'B12', 'B13', 'B14', 'B17', 
                'B25', 'B26', 'B29', 'B30', 'B66', 'B71'
              ],
              '4G (LTE-TDD)': ['B38', 'B41', 'B42', 'B43', 'B48']
            },
            orderCodes: [
              { code: 'RUTM50000000', description: 'Standard package with US PSU' },
              { code: 'RUTM50000100', description: 'Mass packing code' }
            ]
          }
        ],
        footnotes: [
          { id: 1, text: 'For more detailed information about certified carriers, visit our Wiki page' }
        ]
      }
    }
  };
  

  const product = products[productId];
  if (!product || !product.ordering) return null;

  const styles = {
    wrapper: {
      width: '100%',
      padding: '20px',
      maxWidth: '100%',
      overflow: 'hidden'
    },
    section: {
      marginBottom: '32px'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '16px'
    },
    list: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    listItem: {
      marginBottom: '8px',
      color: '#4B5563'
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '16px'
    },
    codeItem: {
      display: 'flex',
      alignItems: 'center'
    },
    codeLabel: {
      fontWeight: '600',
      color: '#374151',
      marginRight: '8px'
    },
    codeValue: {
      color: '#4B5563'
    },
 
  
    scrollHintContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: 'rgba(243, 244, 246, 0.9)',
      padding: '8px 12px',
      borderRadius: '6px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    },
 
    tableWrapper: {
      position: 'relative',
      marginBottom: '24px',
      backgroundColor: '#fff',
      width: '100%',
      overflowX: 'hidden' // Changed from auto to hidden
    },
    scrollIndicator: {
      position: 'absolute',
      right: 0,
      top: 0,
      height: '100%',
      width: '120px',
      background: 'linear-gradient(to left, rgba(255, 255, 255, 0.9) 30%, transparent)',
      display: showScrollHint ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingRight: '16px',
      pointerEvents: 'none',
      zIndex: 10
    },
    scrollHintContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: 'rgba(243, 244, 246, 0.95)',
      padding: '8px 12px',
      borderRadius: '6px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      backdropFilter: 'blur(4px)'
    },
    tableContainer: {
      width: '100%',
      overflowX: 'auto',
      position: 'relative',
      paddingBottom: '6px', // Add some padding for the invisible scrollbar
      '&::-webkit-scrollbar': {
        height: '0px', // Keep scrollbar height at 0 but allow scrolling
        background: 'transparent'
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'transparent'
      },
      scrollbarWidth: 'none', // Firefox
      msOverflowStyle: 'none' // IE and Edge
    },
    scrollHintText: {
      fontSize: '14px',
      color: '#6B7280',
      whiteSpace: 'nowrap'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      tableLayout: 'fixed'
    },
    th: {
      textAlign: 'left',
      backgroundColor: '#F9FAFB',
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      whiteSpace: 'nowrap',
      borderBottom: '1px solid #E5E7EB'
    },
    thHardware: {
      width: '20%'
    },
    thFrequencies: {
      width: '40%'
    },
    thOrderCode: {
      width: '40%'
    },
    td: {
      padding: '12px 16px',
      borderBottom: '1px solid #E5E7EB',
      verticalAlign: 'top',
      fontSize: '14px',
      minWidth: '200px'
    },
    versionInfo: {
      color: '#374151',
      wordBreak: 'break-word'
    },
    regionInfo: {
      fontSize: '13px',
      color: '#6B7280',
      marginTop: '4px',
      wordBreak: 'break-word'
    },
    frequencyContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    frequencyType: {
      fontWeight: '600',
      color: '#374151'
    },
    frequencyBands: {
      color: '#4B5563',
      wordBreak: 'break-word'
    },
    orderCodesContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    orderCodeItem: {
      color: '#4B5563',
      wordBreak: 'break-word'
    },
    footnotes: {
      fontSize: '14px',
      color: '#6B7280',
      marginBottom: '16px'
    },
    footnoteItem: {
      marginBottom: '4px'
    },
    contactText: {
      color: '#4B5563',
      fontSize: '14px'
    },
    contactLink: {
      color: '#2563EB',
      textDecoration: 'none'
    }
  };

  const { ordering } = product;

  return (
    <div style={styles.wrapper}>
      {/* Standard Package Section */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>STANDARD PACKAGE*</h3>
        <ul style={styles.list}>
          {ordering.standardPackage.map(item => (
            <li key={item.id} style={styles.listItem}>
              {item.name}
            </li>
          ))}
        </ul>
      </section>

      {/* Classification Codes Section */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>CLASSIFICATION CODES</h3>
        <div style={styles.gridContainer}>
          {ordering.classificationCodes.map((code, index) => (
            <div key={index} style={styles.codeItem}>
              <span style={styles.codeLabel}>{code.label}:</span>
              <span style={styles.codeValue}>{code.value}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Available Versions Section */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>AVAILABLE VERSIONS</h3>
        <div style={styles.tableWrapper}>
          <div style={styles.scrollIndicator}>
            <div style={styles.scrollHintContent}>
              <span style={styles.scrollHintText}>Scroll to see more</span>
              <ChevronRight size={20} color="#6B7280" />
            </div>
          </div>
          <div ref={tableRef} style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, ...styles.thHardware}}>Hardware <br />version</th>
                  <th style={{...styles.th, ...styles.thFrequencies}}>Supported frequencies</th>
                  <th style={{...styles.th, ...styles.thOrderCode}}>Standard order code / Package Contains</th>
                </tr>
              </thead>
              <tbody>
                {ordering.availableVersions.map((version, index) => (
                  <tr key={index}>
                    <td style={styles.td}>
                      <div style={styles.versionInfo}>{version.hardwareVersion}</div>
                      <div style={styles.regionInfo}>{version.regions.join(', ')}</div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.frequencyContainer}>
                        {Object.entries(version.frequencies).map(([type, bands]) => (
                          <div key={type}>
                            <span style={styles.frequencyType}>{type}:</span>{' '}
                            <span style={styles.frequencyBands}>{bands.join(', ')}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.orderCodesContainer}>
                        {version.orderCodes.map((orderCode, idx) => (
                          <div key={idx} style={styles.orderCodeItem}>
                            {orderCode.code} / {orderCode.description}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Footnotes Section */}
      <div style={styles.footnotes}>
        {ordering.footnotes.map(note => (
          <div key={note.id} style={styles.footnoteItem}>
            {note.id} - {note.text}
          </div>
        ))}
      </div>

      <p style={styles.contactText}>
        For more information on all available packaging options – please{' '}
        <a href="#contact" style={styles.contactLink}>
          contact us
        </a>{' '}
        directly.
      </p>
    </div>
  );
};

export default OrderingTab;